import { Autocomplete, Button, Card, CardContent, Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { findOption, optionValuesEqual, renderOptionWithHighlight, searchTextField } from "utils/searchableOptions";
import { Box } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import ConfigurationOption from "components/ConfigurationOption";
import SAMLMetadataDialog from "components/SAMLMetadataDialog";
import Loading from "components/Loading";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "providers/Notification";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AddOutlined, CancelOutlined, InfoOutlined, MoreHorizOutlined, SaveOutlined } from "@mui/icons-material";

function isValidHttpsUrl(s: string): boolean {
    try {
        const u = new URL(s);
        return u.protocol === "https:" && u.hostname.length > 0;
    } catch {
        return false;
    }
}

const authMethodOptions = [
    { value: "none", label: "No Authentication" },
    { value: "proxy", label: "Proxy" },
    { value: "saml", label: "SAML" },
];
const timeUnitOptions = [
    { value: "minutes", label: "minutes" },
    { value: "hours", label: "hours" },
    { value: "days", label: "days" },
];
const proxyTimeoutBehaviorOptions = [
    { value: "no_block", label: "Don't wait (enrich in background)" },
    { value: "wait_fail_open", label: "Wait for groups, then allow" },
];

function IcapConfiguration() {
    const notification = useNotification();
    const login = useLogin();
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [showTooltipPAC, setShowTooltipPAC] = useState(false);
    const [enableSAML, setEnableSAML] = useState(false);



    const [SAMLMetadataUrl, setSAMLMetadataUrl] = useState("");
    const [SAMLMetadata, setSAMLMetadata] = useState("");


    const [UserSAMLMetadataUrl, setUserSAMLMetadataUrl] = useState("");
    const [UserSAMLMetadata, setUserSAMLMetadata] = useState("");
    const [icapSamlUsernameAttribute, setIcapSamlUsernameAttribute] = useState("username");

    const [proxyUsernameHeader, setProxyUsernameHeader] = useState("X-Authenticated-User");
    const [proxyAuthTimeoutBehavior, setProxyAuthTimeoutBehavior] = useState("no_block");
    const [proxyAuthEnrichmentTimeoutSeconds, setProxyAuthEnrichmentTimeoutSeconds] = useState(5);
    const [enableProxyUserGroupsHeader, setEnableProxyUserGroupsHeader] = useState(false);
    const [proxyUserGroupsHeader, setProxyUserGroupsHeader] = useState("X-Forwarded-Groups");
    const [proxyUserGroupsHeaderDelimiter, setProxyUserGroupsHeaderDelimiter] = useState(",");

    const [samlDialogOpen, setSamlDialogOpen] = useState(false);
    const [userSamlDialogOpen, setUserSamlDialogOpen] = useState(false);

    const [enableActivitylogClear, setEnableActivitylogClear] = useState(false);
    const [activityMaxAge, setActivityMaxAge] = useState<number>();
    const [schedule, setActivitySchedule] = useState("");

    const [enableActivity, setEnableActivity] = useState(false);

    const [userSamlAvailable, setUserSamlAvailable] = useState(false);
    const [userSamlNotAvailableReason, setUserSamlNotAvailableReason] = useState("");

    const [allowedUrlPatterns, setAllowedUrlPatterns] = useState<string[]>([]);

    const [enabledAllowlists, setEnabledAllowlists] = useState<string[]>([]);
    const [availableAllowlists, setAvailableAllowlists] = useState<PremadeAllowlist[]>([]);



    const [authenticationMethod, setAuthenticationMethod] = useState("none");
    const [authSessionExpiration, setAuthSessionExpiration] = useState(30);
    const [authSessionExpirationUnit, setAuthSessionExpirationUnit] = useState("days");
    const [authIdleSessionExpiration, setAuthIdleSessionExpiration] = useState(1);
    const [authIdleSessionExpirationUnit, setAuthIdleSessionExpirationUnit] = useState("days");
    const [samlSelfLogoutUrl, setSamlSelfLogoutUrl] = useState("https://nullafi.com/icap/logout");

    const req = login.GetAxios();

    const loadData = async () => {
        setFinishedLoading(false);
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            const respConfig = await req.get("/api/config");
            const respSamlAvailability = await req.get("/api/config/samlavailability");
            const respAllowlists = await req.get("/api/config/allowlists");

            const settings = respConfig.data;
            const samlAvailability = respSamlAvailability.data;
            const allowLists = respAllowlists.data;

            setUserSamlAvailable(samlAvailability?.user?.available || false);
            setUserSamlNotAvailableReason(samlAvailability?.user?.reason || "");

            setEnabled(settings.enabled);
            setEnableSAML(settings.saml.enable);
            setSAMLMetadata(settings.saml.metadata);

            setProxyUsernameHeader(settings.proxyUsernameHeader || "X-Authenticated-User");
            setProxyAuthTimeoutBehavior(settings.proxyAuthTimeoutBehavior || "no_block");
            setProxyAuthEnrichmentTimeoutSeconds(settings.proxyAuthEnrichmentTimeoutSeconds ?? 5);
            setEnableProxyUserGroupsHeader(settings.enableProxyUserGroupsHeader || false);
            setProxyUserGroupsHeader(settings.proxyUserGroupsHeader || "X-Forwarded-Groups");
            setProxyUserGroupsHeaderDelimiter(settings.proxyUserGroupsHeaderDelimiter || ",");
            setActivitySchedule(settings.activity.cleanupSchedule);
            setActivityMaxAge(Math.round(settings.activity.maxAge / 86400));
            setEnableActivitylogClear((settings.activity.maxAge && settings.activity.cleanupSchedule) ? true : false);
            setEnableActivity(settings.activity.enable);
            setUserSAMLMetadata(settings.userSAMLMetadata || "");
            setIcapSamlUsernameAttribute(settings.icapSamlUsernameAttribute || "username");
            setAuthenticationMethod(settings.authenticationMode || "none");
            setAllowedUrlPatterns(settings.allowWithoutAuthentication || []);
            setAvailableAllowlists(allowLists || []);
            setEnabledAllowlists(settings.enabledAuthenticationAllowLists || []);

            setAuthIdleSessionExpiration(settings.authIdleSessionExpiration || 1);
            setAuthIdleSessionExpirationUnit(settings.authIdleSessionExpirationUnit || "days");
            setAuthSessionExpiration(settings.authSessionExpiration || 30);
            setAuthSessionExpirationUnit(settings.authSessionExpirationUnit || "days");
            setSamlSelfLogoutUrl(settings.samlSelfLogoutUrl || "https://nullafi.com/icap/logout");

            setFinishedLoading(true);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return;
            }

            notification.Display({
                title: "Error",
                message: "Error fetching configuration data",
                type: "error",
            });
        }
    };

    useEffect(() => {
        loadData().catch(console.log);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const SaveConfiguration = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }

        if (userSamlNotAvailableReason !== "" && authenticationMethod === "saml") {
            notification.Display({
                title: "Error",
                message: "To use SAML login, TLS must be enabled.",
                type: "error",
            });
            return;
        }

        if (authenticationMethod === "saml" && !isValidHttpsUrl(samlSelfLogoutUrl)) {
            notification.Display({
                title: "Error",
                message: "Self-service Logout URL must be a valid HTTPS URL.",
                type: "error",
            });
            return;
        }

        if (authenticationMethod === "proxy" && enableProxyUserGroupsHeader && !proxyUserGroupsHeader.trim()) {
            notification.Display({
                title: "Error",
                message: "User Groups header is required when the User Groups header option is enabled.",
                type: "error",
            });
            return;
        }

        if (enableActivitylogClear) {
            if (!activityMaxAge) {
                notification.Display({
                    title: "Error",
                    message: "Max age is required",
                    type: "error",
                });
                return;
            }
            if (!schedule) {
                notification.Display({
                    title: "Error",
                    message: "Schedule is required",
                    type: "error",
                });
                return;
            }
        }

        setSaving(true);

        try {
            const originalConfig = (await req.get("/api/config")).data;

            await req.put("/api/config", {
                ...originalConfig,
                enabled,
                saml: {
                    enable: enableSAML,
                    metadataUrl: SAMLMetadataUrl,
                    metadata: SAMLMetadata,
                },
                proxyUsernameHeader,
                proxyAuthTimeoutBehavior,
                proxyAuthEnrichmentTimeoutSeconds,
                enableProxyUserGroupsHeader,
                proxyUserGroupsHeader,
                proxyUserGroupsHeaderDelimiter,
                activity: {
                    ...originalConfig.activity,
                    enable: enableActivity,
                    maxAge: enableActivitylogClear ? ((activityMaxAge || 0) * 86400) : undefined,
                    cleanupSchedule: enableActivitylogClear ? schedule : undefined,
                },
                userSAMLMetadata: UserSAMLMetadata,
                userSAMLMetadataURL: UserSAMLMetadataUrl,
                icapSamlUsernameAttribute,
                authenticationMode: authenticationMethod,
                allowWithoutAuthentication: allowedUrlPatterns,
                enabledAuthenticationAllowLists: enabledAllowlists,
                authSessionExpiration,
                authSessionExpirationUnit,
                authIdleSessionExpiration,
                authIdleSessionExpirationUnit,
                samlSelfLogoutUrl,
            });
            notification.Display({
                type: "success",
                title: "Success",
                message: "ICAP Configuration saved successfully",
            });
        } catch (err) {
            //not an early return: setSaving(false) runs after this block, and
            //leaving the button spinning through the redirect is worse
            if (!IsUnauthorizedError(err)) {
                notification.Display({
                    title: "Error",
                    message: "Error saving configuration",
                    type: "error",
                });
            }
        }

        setSaving(false);
        void loadData();
    };

    const clearUserSessions = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            await req.post("/api/users/deleteall", {});
            notification.Display({
                title: "Success",
                message: "All user sessions cleared",
                type: "success",
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Error deleting user sessions",
                type: "error",
            });
        }
    };


    return <Loading finished={finishedLoading}>
        <Typography variant="h1">ICAP Configuration</Typography>

        <Box display="flex" flexDirection="column">

            <Card sx={{ marginTop: "30px", padding: "0px" }}>
                <CardContent sx={{ padding: "10px" }}>
                    <ConfigurationOption
                        label="Proxy Configuration"
                        description="You can download nullafi certificate and PAC file to use our proxy."
                        hideCheckbox={true}
                    >
                        <Stack marginTop="10px" direction="row" spacing={2}>
                            <Button variant="outlined" href="/api/proxy/download/certificate/nullafi.crt" target="_blank">Download Certificate</Button>
                            <Button variant="outlined" href="/api/proxy/download/pac/nullafi.pac" target="_blank">Download PAC File</Button>
                            <TextField
                                id="pac-url"
                                label="PAC File URL"
                                value={`${window.origin}/api/proxy/download/pac/nullafi.pac`}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment:
                                        <InputAdornment position="end" >
                                            <Tooltip
                                                PopperProps={{
                                                    disablePortal: true,
                                                }}
                                                open={showTooltipPAC}
                                                disableFocusListener
                                                disableHoverListener
                                                disableTouchListener
                                                title="Copied!"
                                            >
                                                <IconButton
                                                    id="copy-pac-url-button"
                                                    color="primary"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${window.origin}/api/proxy/download/pac/nullafi.pac`).catch(console.log);
                                                        setShowTooltipPAC(true);
                                                        setTimeout(() => {
                                                            setShowTooltipPAC(false);
                                                        }, 2000);
                                                    }}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                                sx={{ marginTop: "10px", marginBottom: "20px" }}
                                inputProps={{ style: { width: "380px" } }}

                            />
                        </Stack>
                    </ConfigurationOption>

                    <Divider sx={{ marginTop: "10px", marginBottom: "10px" }} />
                    <ConfigurationOption
                        label="Security"
                        description="Users can be authenticated for better access control"
                        hideCheckbox={true}
                    >
                        <Grid container>
                            <Grid item sm={12} >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    flexDirection: "row"
                                }}>
                                    <FormControl sx={{ marginTop: "20px", minWidth: "300px" }}>
                                        <Autocomplete
                                            fullWidth
                                            disableClearable={true as boolean}
                                            id="authentication-method-selector"
                                            options={authMethodOptions}
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={optionValuesEqual}
                                            value={findOption(authMethodOptions, authenticationMethod || null)}
                                            onChange={(_, option) => setAuthenticationMethod(option?.value || "")}
                                            renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(authMethodOptions, authenticationMethod || null)?.label)}
                                            renderInput={(params) => searchTextField(params, { label: "Authentication Method" })}
                                        />
                                        {userSamlNotAvailableReason !== "" && authenticationMethod === "saml" ? <Typography variant="caption" color="error.main">{userSamlNotAvailableReason}</Typography> : undefined}
                                    </FormControl>
                                    {authenticationMethod === "proxy" &&
                                        <Box sx={{ marginLeft: "20px", marginTop: "20px", display: "flex", flexDirection: "row", gap: "20px" }}>
                                            <FormControl sx={{ minWidth: "300px" }}>
                                                <TextField
                                                    id="proxy-username-header-input"
                                                    label="Username header"
                                                    variant="outlined"
                                                    value={proxyUsernameHeader}
                                                    onChange={(x) => setProxyUsernameHeader(x.target.value)}
                                                />
                                            </FormControl>
                                        </Box>
                                    }
                                    {authenticationMethod === "saml" && <>
                                        <FormControl sx={{ marginLeft: "20px", marginTop: "20px", minWidth: "300px" }}>
                                            <Stack direction="row" spacing={2}>
                                                <TextField
                                                    label="Session Timeout"
                                                    type="number"
                                                    value={authIdleSessionExpiration}
                                                    onChange={(x) => {
                                                        setAuthIdleSessionExpiration(Number(x.target.value || 0));
                                                    }}
                                                />
                                                <Autocomplete
                                                    disableClearable={true as boolean}
                                                    sx={{ width: "100px" }}
                                                    options={timeUnitOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    isOptionEqualToValue={optionValuesEqual}
                                                    value={findOption(timeUnitOptions, authIdleSessionExpirationUnit || null)}
                                                    onChange={(_, option) => setAuthIdleSessionExpirationUnit((option?.value || "minutes") as any)}
                                                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(timeUnitOptions, authIdleSessionExpirationUnit || null)?.label)}
                                                    renderInput={(params) => searchTextField(params, { hideSearchIcon: true })}
                                                />
                                            </Stack>
                                        </FormControl>
                                        <FormControl sx={{ marginLeft: "20px", marginTop: "20px", minWidth: "300px" }}>
                                            <Stack direction="row" spacing={2}>
                                                <TextField
                                                    label="Max. Session Duration"
                                                    type="number"
                                                    value={authSessionExpiration}
                                                    onChange={(x) => {
                                                        setAuthSessionExpiration(Number(x.target.value || 0));
                                                    }}
                                                />
                                                <Autocomplete
                                                    disableClearable={true as boolean}
                                                    sx={{ width: "100px" }}
                                                    options={timeUnitOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    isOptionEqualToValue={optionValuesEqual}
                                                    value={findOption(timeUnitOptions, authSessionExpirationUnit || null)}
                                                    onChange={(_, option) => setAuthSessionExpirationUnit((option?.value || "minutes") as any)}
                                                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(timeUnitOptions, authSessionExpirationUnit || null)?.label)}
                                                    renderInput={(params) => searchTextField(params, { hideSearchIcon: true })}
                                                />
                                            </Stack>
                                        </FormControl>
                                    </>
                                    }
                                </Box>
                            </Grid>
                            {authenticationMethod === "proxy" &&
                                <Grid item sm={12} sx={{ marginTop: "15px" }}>
                                    <Stack direction="column" spacing={2}>
                                        <Stack direction="column" spacing={2}>
                                            <FormControl>
                                                <FormControlLabel
                                                    sx={{ marginLeft: 0 }}
                                                    control={
                                                        <Checkbox
                                                            id="proxy-user-groups-header-enable"
                                                            checked={enableProxyUserGroupsHeader}
                                                            onChange={(x) => setEnableProxyUserGroupsHeader(x.target.checked)}
                                                        />
                                                    }
                                                    label="Enable User Groups header"
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ marginLeft: "14px", marginBottom: "20px" }}>
                                                    When enabled, user groups are read from the request headers and the directory integration is not queried for group membership.
                                                </Typography>
                                            </FormControl>
                                            {enableProxyUserGroupsHeader ? 
                                                <Stack direction="row" spacing={2}>
                                                    <FormControl sx={{ width: "300px" }}>
                                                        <TextField
                                                            id="proxy-user-groups-header-input"
                                                            label="User Groups header"
                                                            variant="outlined"
                                                            required
                                                            error={!proxyUserGroupsHeader.trim()}
                                                            helperText={!proxyUserGroupsHeader.trim() ? "Required when the User Groups header option is enabled" : " "}
                                                            value={proxyUserGroupsHeader}
                                                            onChange={(x) => setProxyUserGroupsHeader(x.target.value)}
                                                        />
                                                    </FormControl>
                                                    <FormControl sx={{ width: "300px" }}>
                                                        <TextField
                                                            id="proxy-user-groups-header-delimiter-input"
                                                            label="Delimiter"
                                                            variant="outlined"
                                                            helperText="Separator between group values in the header"
                                                            value={proxyUserGroupsHeaderDelimiter}
                                                            onChange={(x) => setProxyUserGroupsHeaderDelimiter(x.target.value)}
                                                        />
                                                    </FormControl>
                                                </Stack>
                                                :
                                                <>
                                                    <Stack direction="row" spacing={2}>
                                                        <FormControl sx={{ minWidth: "300px" }}>
                                                            <Stack direction="row" spacing={2}>
                                                                <TextField
                                                                    label="Session Timeout"
                                                                    type="number"
                                                                    value={authIdleSessionExpiration}
                                                                    onChange={(x) => {
                                                                        setAuthIdleSessionExpiration(Number(x.target.value || 0));
                                                                    }}
                                                                />
                                                                <Autocomplete
                                                                    disableClearable={true as boolean}
                                                                    sx={{ width: 120 }}
                                                                    options={timeUnitOptions}
                                                                    getOptionLabel={(option) => option.label}
                                                                    isOptionEqualToValue={optionValuesEqual}
                                                                    value={findOption(timeUnitOptions, authIdleSessionExpirationUnit || null)}
                                                                    onChange={(_, option) => setAuthIdleSessionExpirationUnit((option?.value || "minutes") as any)}
                                                                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(timeUnitOptions, authIdleSessionExpirationUnit || null)?.label)}
                                                                    renderInput={(params) => searchTextField(params, { hideSearchIcon: true })}
                                                                />
                                                            </Stack>
                                                        </FormControl>
                                                        <FormControl sx={{ marginLeft: "20px", marginTop: "20px", minWidth: "300px" }}>
                                                            <Stack direction="row" spacing={2}>
                                                                <TextField
                                                                    label="Max. Session Duration"
                                                                    type="number"
                                                                    value={authSessionExpiration}
                                                                    onChange={(x) => {
                                                                        setAuthSessionExpiration(Number(x.target.value || 0));
                                                                    }}
                                                                />
                                                                <Autocomplete
                                                                    disableClearable={true as boolean}
                                                                    sx={{ width: 120 }}
                                                                    options={timeUnitOptions}
                                                                    getOptionLabel={(option) => option.label}
                                                                    isOptionEqualToValue={optionValuesEqual}
                                                                    value={findOption(timeUnitOptions, authSessionExpirationUnit || null)}
                                                                    onChange={(_, option) => setAuthSessionExpirationUnit((option?.value || "minutes") as any)}
                                                                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(timeUnitOptions, authSessionExpirationUnit || null)?.label)}
                                                                    renderInput={(params) => searchTextField(params, { hideSearchIcon: true })}
                                                                />
                                                            </Stack>
                                                        </FormControl>
                                                    </Stack>

                                                    <Stack direction="row" spacing={2}>
                                                        <FormControl sx={{ minWidth: "400px" }}>
                                                            <Autocomplete
                                                                id="proxy-timeout-behavior-selector"
                                                                disableClearable={true as boolean}
                                                                options={proxyTimeoutBehaviorOptions}
                                                                getOptionLabel={(option) => option.label}
                                                                isOptionEqualToValue={optionValuesEqual}
                                                                value={findOption(proxyTimeoutBehaviorOptions, proxyAuthTimeoutBehavior || null)}
                                                                onChange={(_, option) => setProxyAuthTimeoutBehavior((option?.value || "no_block") as any)}
                                                                renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(proxyTimeoutBehaviorOptions, proxyAuthTimeoutBehavior || null)?.label)}
                                                                renderInput={(params) => searchTextField(params, { label: "On new user" })}
                                                            />
                                                        </FormControl>
                                                        {proxyAuthTimeoutBehavior === "wait_fail_open" &&
                                                            <FormControl sx={{ minWidth: "300px" }}>
                                                                <TextField
                                                                    id="proxy-enrichment-timeout-input"
                                                                    label="Max wait for groups (seconds)"
                                                                    type="number"
                                                                    value={proxyAuthEnrichmentTimeoutSeconds}
                                                                    onChange={(x) => setProxyAuthEnrichmentTimeoutSeconds(Number(x.target.value || 0))}
                                                                />
                                                            </FormControl>
                                                        }
                                                    </Stack>
                                                </>
                                            }
                                        </Stack>



                                    </Stack>
                                </Grid>
                            }
                            {authenticationMethod === "saml" && <>
                                <Grid item sm={12} sx={{ marginTop: "15px" }}>
                                    <TextField
                                        size="small"
                                        label="Username Attribute"
                                        helperText="The SAML attribute to use as the username. Use 'NameID' to use the SAML Subject."
                                        value={icapSamlUsernameAttribute}
                                        onChange={(e) => setIcapSamlUsernameAttribute(e.target.value)}
                                        placeholder="username"
                                        sx={{ minWidth: "300px" }}
                                    />
                                </Grid>
                                <Grid item sm={12} sx={{ marginTop: "15px" }}>
                                    <TextField
                                        size="small"
                                        label="Self-service Logout URL"
                                        helperText="URL users can visit through the proxy to log themselves out. Must be HTTPS."
                                        value={samlSelfLogoutUrl}
                                        onChange={(e) => setSamlSelfLogoutUrl(e.target.value)}
                                        placeholder="https://nullafi.com/icap/logout"
                                        sx={{ minWidth: "400px" }}
                                        error={!isValidHttpsUrl(samlSelfLogoutUrl)}
                                    />
                                </Grid>
                                <Grid item sm={12}>
                                    <Stack sx={{ maxWidth: "250px" }}>
                                        <Button
                                            id="saml-user-open-dialog"
                                            sx={{ marginTop: "10px" }}
                                            onClick={() => {
                                                setUserSamlDialogOpen(true);
                                            }}
                                            variant="outlined"
                                            disabled={!userSamlAvailable}
                                        >
                                            Set IDp Metadata
                                        </Button>
                                    </Stack>
                                    {userSamlAvailable && <Link sx={{ marginLeft: "10px" }} href="/user/saml/metadata" download>Download SP Metadata</Link>}
                                </Grid>
                                <Grid item sm={12}>
                                    <Button
                                        sx={{ marginTop: "10px" }}
                                        id="clear_user_sessions"
                                        onClick={() => {
                                            clearUserSessions().catch(console.log);
                                        }}
                                        variant="outlined"
                                        disabled={!userSamlAvailable}
                                    >
                                        Clear User Sessions
                                    </Button>
                                </Grid>

                            </>}
                        </Grid>
                    </ConfigurationOption>

                    {authenticationMethod === "saml" && userSamlNotAvailableReason === "" && <>
                        <Divider sx={{ marginTop: "10px", marginBottom: "10px" }} />
                        <ConfigurationOption
                            label="Allowed URLs"
                            description="Access for URLs that match patterns on this list will be allowed even if the user is not authenticated"
                            hideCheckbox={true}
                        >
                            <Grid container sx={{ maxWidth: "600px", }}>
                                <Grid item sm={12}>
                                    <PremadeAllowlistsSelector
                                        availableAllowLists={availableAllowlists}
                                        enabledAllowLists={enabledAllowlists}
                                        toggledAllowList={(id, newstate) => {
                                            if (newstate) {
                                                setEnabledAllowlists(enabledAllowlists.concat(id));
                                            } else {
                                                setEnabledAllowlists(enabledAllowlists.filter(x => x !== id));
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item sm={12}>
                                    <Typography>Additional allowed urls:</Typography>
                                </Grid>
                                <AllowedUrlList
                                    allowedUrls={allowedUrlPatterns}
                                    valueChanged={(x) => setAllowedUrlPatterns(x)}
                                />
                            </Grid>
                        </ConfigurationOption>
                    </>}
                </CardContent>
            </Card>
        </Box >


        <Box display="flex" sx={{ marginTop: "50px", marginBottom: "50px" }}>
            <LoadingButton
                id="save-changes-button"
                variant="contained"
                sx={{ marginLeft: "auto" }}
                onClick={SaveConfiguration}
                loading={saving}>Save Changes</LoadingButton>
        </Box>

        <SAMLMetadataDialog
            open={samlDialogOpen}
            onClose={() => {
                setSamlDialogOpen(false);
            }}
            value={(SAMLMetadata && { type: "xml", metadata: SAMLMetadata }) || (SAMLMetadataUrl && { type: "url", url: SAMLMetadataUrl }) || undefined}
            onSave={(v) => {
                setSamlDialogOpen(false);
                if (v.type === "url") {
                    setSAMLMetadata("");
                    setSAMLMetadataUrl(v.url);
                } else if (v.type === "xml") {
                    setSAMLMetadata(v.metadata);
                    setSAMLMetadataUrl("");
                }
            }}
        />

        <SAMLMetadataDialog
            open={userSamlDialogOpen}
            onClose={() => {
                setUserSamlDialogOpen(false);
            }}
            value={(UserSAMLMetadata && { type: "xml", metadata: UserSAMLMetadata }) || (UserSAMLMetadataUrl && { type: "url", url: UserSAMLMetadataUrl }) || undefined}
            onSave={(v) => {
                setUserSamlDialogOpen(false);
                if (v.type === "url") {
                    setUserSAMLMetadata("");
                    setUserSAMLMetadataUrl(v.url);
                } else if (v.type === "xml") {
                    setUserSAMLMetadata(v.metadata);
                    setUserSAMLMetadataUrl("");
                }
            }}
        />
    </Loading >;
}



interface PremadeAllowlistsSelectorProps {
    availableAllowLists: PremadeAllowlist[]
    enabledAllowLists?: string[]
    toggledAllowList?: (id: string, state: boolean) => void
}

interface PremadeAllowlist {
    id: string
    name: string
    patterns: string[]
}

function PremadeAllowlistsSelector(props: PremadeAllowlistsSelectorProps) {
    return <>
        <List sx={{ width: "100%" }}>
            {props.availableAllowLists.map((value) => {
                return (
                    <AllowListItem
                        value={value}
                        checked={(props.enabledAllowLists && props.enabledAllowLists.includes(value.id)) || false}
                        toggledAllowList={() => {
                            props.toggledAllowList && props.toggledAllowList(value.id, !(props.enabledAllowLists?.includes(value.id) || false));
                        }}
                    />
                );
            })}
        </List>
    </>;
}

interface AllowListItemProps {
    value: PremadeAllowlist
    checked: boolean
    toggledAllowList?: () => void
}

function AllowListItem(props: AllowListItemProps) {
    const labelId = `allowlist-label-${props.value.id}`;
    const ref = useRef(null);
    const [open, setOpen] = useState(false);

    return <ListItem
        key={`allowlist-item-${props.value.id}`}
        secondaryAction={
            <IconButton id={`allowlist-item-details-${props.value.id}`} ref={ref} edge="end" onClick={() => setOpen(true)}>
                <InfoOutlined />
            </IconButton>
        }
        disablePadding
    >
        <ListItemButton role={undefined} onClick={() => props.toggledAllowList && props.toggledAllowList()}>
            <ListItemIcon>
                <Checkbox
                    id={`allowlist-item-checkbox-${props.value.id}`}
                    edge="start"
                    checked={props.checked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                />
            </ListItemIcon>
            <ListItemText id={labelId} primary={props.value.name} />
        </ListItemButton>
        <Popover
            open={open}
            anchorEl={ref.current}
            onClose={() => setOpen(false)}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
        >
            <Box sx={{
                padding: "10px"
            }}>
                <Typography sx={{ fontWeight: 600 }}>Allowed url patterns:</Typography>
                {props.value.patterns && props.value.patterns.map(x => {
                    return <Typography>{x}</Typography>;
                })}
            </Box>
        </Popover>
    </ListItem>;
}


interface AllowedUrlListProps {
    allowedUrls: string[]
    valueChanged: (newValue: string[]) => void
}

function AllowedUrlList(props: AllowedUrlListProps) {
    const [allowedUrlInput, setAllowedUrlInput] = useState("");
    const [editingItem, setEditingItem] = useState(-1);


    useEffect(() => {
        setAllowedUrlInput("");
        setEditingItem(-1);
    }, [props.allowedUrls]);

    return <Grid item sm={12}>
        <List sx={{ width: "100%" }}>
            {props.allowedUrls.map((value, i) => {
                return <AllowedUrlListItem
                    index={`${i}`}
                    value={value}
                    onDelete={() => {
                        props.valueChanged(props.allowedUrls.filter((x, idx) => i !== idx));
                    }}
                    onEditFinished={(newvalue) => {
                        props.valueChanged(props.allowedUrls.map((original, idx) => {
                            if (idx === i) {
                                return newvalue;
                            }
                            return original;
                        }));
                    }}
                />;
            })}
            {props.allowedUrls.length === 0 ? <Typography sx={{ "padding": "10px", textAlign: "center" }}>No items to display</Typography> : undefined}
            <ListItem
                secondaryAction={
                    <IconButton id="allowed-url-list-add-button" edge="end" aria-label="comments"
                        disabled={editingItem !== -1}
                        onClick={() => {
                            if (allowedUrlInput === "") {
                                return;
                            }

                            props.valueChanged(props.allowedUrls.concat(allowedUrlInput));
                            setAllowedUrlInput("");
                        }}
                    >
                        <AddOutlined />
                    </IconButton>
                }

            >
                <TextField fullWidth id="allowed-url-list-add-input" variant="outlined"
                    disabled={editingItem !== -1}
                    value={allowedUrlInput}
                    onChange={x => {
                        setAllowedUrlInput(x.target.value);
                    }}
                />
            </ListItem>
        </List>
    </Grid>;
}

interface AllowedUrlListItemProps {
    index: string
    value: string
    onDelete: () => void
    onEditFinished: (newValue: string) => void
}

function AllowedUrlListItem(props: AllowedUrlListItemProps) {
    const [inputValue, setInputValue] = useState("");
    const [editMode, setEditMode] = useState(false);
    const ref = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);


    useEffect(() => {
        setInputValue(props.value);
    }, [props.value]);

    return <>
        {!editMode ?
            <ListItem
                secondaryAction={
                    <IconButton id={`allowed-url-list-menu-${props.index}`} ref={ref} edge="end"
                        onClick={() => {
                            setMenuOpen(true);
                        }}
                    >
                        <MoreHorizOutlined />
                    </IconButton>
                }

            >
                <ListItemButton>
                    <ListItemText primary={props.value} />
                </ListItemButton>
            </ListItem>
            :
            <ListItem>
                <TextField fullWidth id={`allowed-url-list-input-${props.index}`} variant="outlined"
                    value={inputValue}
                    onChange={x => {
                        setInputValue(x.target.value);
                    }}
                />
                <IconButton
                    id={`allowed-url-list-input-cancel-${props.index}`}
                    onClick={() => {
                        setInputValue(props.value);
                        setEditMode(false);
                    }}
                >
                    <CancelOutlined />
                </IconButton>
                <IconButton
                    id={`allowed-url-list-input-save-${props.index}`}
                    onClick={() => {
                        props.onEditFinished(inputValue);
                        setMenuOpen(false);
                        setEditMode(false);
                    }}
                >
                    <SaveOutlined />
                </IconButton>
            </ListItem>
        }

        <Menu
            anchorEl={ref.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}

        >
            <MenuItem id={`allowed-url-list-menu-edit-${props.index}`} onClick={() => { setEditMode(true); setMenuOpen(false); }}>Edit</MenuItem>
            <MenuItem id={`allowed-url-list-menu-delete-${props.index}`} onClick={props.onDelete}>Delete</MenuItem>
        </Menu>
    </>;


}

export default IcapConfiguration;